import { useState, useEffect, useRef } from 'react'
import { Sparkles, BookOpen, Tv, Users, BarChart2, FolderOpen, Zap, Search, ChevronRight, Star } from 'lucide-react'
import { motion } from 'framer-motion'
import AuthModal from './AuthModal'

const FEATURES = [
  {
    icon: Sparkles,
    color: 'text-accent',
    bg: 'bg-accent/10 border-accent/20',
    title: 'AI Search',
    desc: 'Describe what you want in plain English. "Dark isekai where the MC hides his power" — it finds it.',
  },
  {
    icon: BookOpen,
    color: 'text-green-400',
    bg: 'bg-green-400/10 border-green-400/20',
    title: 'Manga Reader',
    desc: 'MangaDex + 8 external sources. Vertical strip, single, or double-page. Swipe, pinch, offline.',
  },
  {
    icon: Tv,
    color: 'text-blue-400',
    bg: 'bg-blue-400/10 border-blue-400/20',
    title: 'Anime Player',
    desc: 'Multiple sources tried automatically. If one fails, it switches silently.',
  },
  {
    icon: Users,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10 border-purple-400/20',
    title: 'Community',
    desc: 'Follow friends. See what your crew is reading and watching in real time.',
  },
  {
    icon: BarChart2,
    color: 'text-orange-400',
    bg: 'bg-orange-400/10 border-orange-400/20',
    title: 'Stats & Streaks',
    desc: 'Reading heatmaps, streak tracker, achievements. Your reading history, visualised.',
  },
  {
    icon: FolderOpen,
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10 border-yellow-400/20',
    title: 'Reading Lists',
    desc: 'Curate themed collections — "Best isekai", "Summer reads" — and share your taste.',
  },
]

const SEARCHES = [
  'dark fantasy manga where the MC starts weak',
  'completed romance anime with a happy ending',
  'manhwa where the protagonist returns from the future',
  'psychological thriller manga like Monster',
  'isekai with an overpowered but calm protagonist',
]

function TypewriterSearch() {
  const [displayed, setDisplayed] = useState('')
  const [queryIdx, setQueryIdx]   = useState(0)
  const [charIdx, setCharIdx]     = useState(0)
  const [deleting, setDeleting]   = useState(false)
  const pauseRef = useRef(false)

  useEffect(() => {
    const query = SEARCHES[queryIdx]

    if (pauseRef.current) return

    if (!deleting && charIdx < query.length) {
      const t = setTimeout(() => {
        setDisplayed(query.slice(0, charIdx + 1))
        setCharIdx(i => i + 1)
      }, 45 + Math.random() * 35)
      return () => clearTimeout(t)
    }

    if (!deleting && charIdx === query.length) {
      pauseRef.current = true
      const t = setTimeout(() => {
        pauseRef.current = false
        setDeleting(true)
      }, 1800)
      return () => clearTimeout(t)
    }

    if (deleting && charIdx > 0) {
      const t = setTimeout(() => {
        setDisplayed(query.slice(0, charIdx - 1))
        setCharIdx(i => i - 1)
      }, 22)
      return () => clearTimeout(t)
    }

    if (deleting && charIdx === 0) {
      setDeleting(false)
      setQueryIdx(i => (i + 1) % SEARCHES.length)
    }
  }, [charIdx, deleting, queryIdx])

  return (
    <div className="flex items-center gap-3 bg-bg2 border border-border rounded-2xl px-4 py-3 max-w-xl mx-auto">
      <Search size={16} className="text-accent flex-shrink-0" />
      <span className="text-gray-300 text-sm italic min-h-[1.25rem] text-left flex-1">
        {displayed}<span className="animate-pulse">|</span>
      </span>
      <span className="ml-auto flex-shrink-0 text-[10px] font-bold text-accent bg-accent/10 px-2 py-0.5 rounded-full border border-accent/20">AI</span>
    </div>
  )
}

export default function LandingPage() {
  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState('signup')

  const openSignup = () => { setAuthMode('signup'); setAuthOpen(true) }
  const openSignin = () => { setAuthMode('signin'); setAuthOpen(true) }

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-bg/80 backdrop-blur-md border-b border-border/40">
      <div className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-bg3 border border-accent/30 flex items-center justify-center">
            <span className="text-accent font-jp font-bold text-lg" style={{ textShadow: '0 0 10px rgba(56,189,248,0.5)' }}>仲</span>
          </div>
          <div>
            <span className="font-display tracking-widest gradient-text text-sm">NAKAMA</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={openSignin}
            className="text-sm text-gray-400 hover:text-white transition-colors font-medium hidden sm:block">
            Sign in
          </button>
          <button onClick={openSignup}
            className="flex items-center gap-1.5 px-4 py-2 bg-accent text-bg font-bold text-sm rounded-xl hover:opacity-90 transition-all">
            Get started <ChevronRight size={14} />
          </button>
        </div>
      </div>
      </nav>

      {/* Hero */}
      <section className="text-center px-4 pt-16 pb-20 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-bold mb-6">
            <Zap size={11} /> Free to use · No app store needed
          </div>

          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight mb-5">
            Your anime & manga
            <br />
            <span className="gradient-text">crew, all in one place</span>
          </h1>

          <p className="text-gray-400 text-lg max-w-xl mx-auto mb-8">
            Read manga, watch anime, track your library, and discover new titles using AI — all without switching apps.
          </p>

          {/* AI search demo */}
          <div className="mb-8">
            <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Try searching like this:</p>
            <TypewriterSearch />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={openSignup}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-accent text-bg font-bold rounded-xl hover:opacity-90 transition-all text-base">
              Start for free <ChevronRight size={16} />
            </button>
            <button onClick={openSignin}
              className="w-full sm:w-auto px-8 py-3.5 bg-bg3 border border-border text-gray-300 font-semibold rounded-xl hover:border-accent/40 hover:text-white transition-all text-sm">
              Sign in
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-3">No credit card required. Works in your browser.</p>
        </motion.div>
      </section>

      {/* Stats bar */}
      <div className="border-y border-border/50 bg-bg2/50 py-4 mb-16">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-8 sm:gap-16 flex-wrap">
          {[
            { value: '9', label: 'manga sources' },
            { value: '6+', label: 'anime sources' },
            { value: 'AI', label: 'powered search' },
            { value: 'PWA', label: 'any device' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className="font-display text-2xl text-accent">{s.value}</p>
              <p className="text-xs text-gray-500 font-semibold">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 mb-20">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl mb-2">Everything in one app</h2>
          <p className="text-gray-400 text-sm">No more juggling 4 different sites</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, color, bg, title, desc }) => (
            <div key={title} className="bg-bg2 border border-border rounded-2xl p-5 hover:border-border/80 transition-colors">
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-3 ${bg}`}>
                <Icon size={18} className={color} />
              </div>
              <h3 className="font-bold text-sm mb-1">{title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI callout */}
      <section className="max-w-3xl mx-auto px-4 mb-20">
        <div className="bg-gradient-to-br from-accent/5 to-accent/[0.02] border border-accent/20 rounded-3xl p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles size={22} className="text-accent" />
          </div>
          <h2 className="font-display text-2xl mb-3">Search like you think</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
            Stop trying to remember exact titles. Describe what you're looking for and Nakama's AI finds it — across both manga and anime.
          </p>
          <div className="space-y-2 text-left max-w-sm mx-auto">
            {[
              '"romance manga that doesn\'t drag on forever"',
              '"completed isekai with an actually smart MC"',
              '"anime like Attack on Titan but finished"',
            ].map(q => (
              <div key={q} className="flex items-center gap-2.5 bg-bg2 border border-border rounded-xl px-3 py-2">
                <Search size={12} className="text-accent flex-shrink-0" />
                <span className="text-xs text-gray-300 italic">{q}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-3xl mx-auto px-4 mb-20">
        <div className="text-center mb-8">
          <h2 className="font-display text-3xl mb-2">Free to start</h2>
          <p className="text-gray-400 text-sm">Upgrade when you want more</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-bg2 border border-border rounded-2xl p-6">
            <p className="font-bold text-lg mb-1">Free</p>
            <p className="font-display text-3xl mb-4">£0</p>
            <ul className="space-y-2 text-sm text-gray-400">
              {['Manga reader (all sources)', 'Anime player (all sources)', 'Library & tracking', '1 AI search/day', 'Community & follows'].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-bg3 border border-border flex items-center justify-center flex-shrink-0">
                    <span className="text-[8px] text-gray-500">✓</span>
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={openSignup}
              className="w-full mt-6 py-2.5 bg-bg3 border border-border text-gray-200 font-semibold rounded-xl text-sm hover:border-accent/40 hover:text-white transition-all">
              Get started free
            </button>
          </div>
          <div className="bg-bg2 border-2 border-accent rounded-2xl p-6 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-accent text-bg text-xs font-bold px-3 py-1 rounded-full">Most popular</span>
            </div>
            <p className="font-bold text-lg mb-1">Pro</p>
            <p className="font-display text-3xl mb-4">£3.99<span className="text-base font-normal text-gray-400">/mo</span></p>
            <ul className="space-y-2 text-sm text-gray-300">
              {[
                'Everything in Free',
                'Unlimited AI search',
                'AI Source Finder',
                'Stats & achievements',
                'Reading lists',
                'Offline downloads — coming soon',
                'Chapter drop alerts',
              ].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center flex-shrink-0">
                    <Star size={7} className="text-accent" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={openSignup}
              className="w-full mt-6 py-2.5 bg-accent text-bg font-bold rounded-xl text-sm hover:opacity-90 transition-all">
              Start free, upgrade anytime
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="text-center px-4 py-20 border-t border-border/50">
        <h2 className="font-display text-3xl mb-3">Join the crew</h2>
        <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
          Free to use. No app store. Works on any phone, tablet, or desktop.
        </p>
        <button onClick={openSignup}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-bg font-bold rounded-xl hover:opacity-90 transition-all">
          Create free account <ChevronRight size={16} />
        </button>
        <p className="text-xs text-gray-600 mt-3">Already have an account?{' '}
          <button onClick={openSignin} className="text-accent hover:opacity-80 transition-opacity">Sign in</button>
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 px-4 py-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="text-accent font-jp font-bold">仲</span>
          <span className="font-display tracking-widest text-xs text-gray-500">NAKAMA</span>
        </div>
        <p className="text-[11px] text-gray-700">Your anime & manga companion</p>
        <a href="mailto:hello@nakamaapp.co" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors mt-1 block">hello@nakamaapp.co</a>
      </footer>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} />
    </div>
  )
}
